
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { isSSR } from "@/lib/isSSR";
import { getCachedImagePath } from "@/services/imageCache/imageCacheService";

interface BlogPostFeaturedImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function BlogPostFeaturedImage({ 
  src, 
  alt,
  className 
}: BlogPostFeaturedImageProps) {
  // If no source, show placeholder
  if (!src) {
    return (
      <div className={cn("w-full h-64 bg-muted flex items-center justify-center rounded-lg mb-6", className)}>
        <p className="text-muted-foreground">Immagine non disponibile</p>
      </div>
    );
  }
  
  // Special handling for the first blog post if it has issues
  if (src.includes('wp-content/uploads/2023/01/')) {
    console.log('[BlogPostFeaturedImage] Using fallback for problematic first post image');
    src = '/placeholder.svg';
  }
  
  // Process the image URL for server-side rendering
  let imageSrc = src;
  
  // Only process the URL if it's a WordPress image
  if (src.startsWith('http') && (src.includes('blog.gildy.it') || src.includes('www.gildy.it'))) {
    try {
      // Use a fixed size for server-side rendering
      const imageWidth = 1200;
      const quality = 85;
      
      // Get the cached path
      const cachedPath = getCachedImagePath(src);
      
      // Add optimization parameters
      if (cachedPath !== src) {
        imageSrc = `${cachedPath}&w=${imageWidth}&q=${quality}&f=webp`;
      }
      
      console.log(`[BlogPostFeaturedImage] Server-side image processing: ${src} -> ${imageSrc}`);
    } catch (e) {
      console.error("[BlogPostFeaturedImage] Error processing image URL:", e);
      // Keep the original URL if there's an error
    }
  }
  
  return (
    <Card className={cn("overflow-hidden mb-6 p-0 border-0", className)}>
      <img 
        src={imageSrc}
        alt={alt} 
        width={800}
        height={400}
        loading="lazy"
        decoding="async"
        className="w-full h-auto max-h-[500px] object-cover rounded-lg"
        onError={(e) => {
          console.error("[BlogPostFeaturedImage] Failed to load image:", imageSrc);
          // Set a fallback image on error
          if (e.currentTarget.src !== '/placeholder.svg') {
            e.currentTarget.src = '/placeholder.svg';
          }
        }}
      />
    </Card>
  );
}
